import { useEffect, useId, useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { cn } from '@/lib/cn';
import { spring } from '@/lib/motion';
import { IconButton } from './Button';

/** Open overlays, newest last: only the top one handles Escape and Tab (a sheet can open another). */
const stack: object[] = [];

function useFocusTrap(open: boolean, onClose: (() => void) | undefined, ref: React.RefObject<HTMLDivElement>) {
  useEffect(() => {
    if (!open) return;
    const me = {};
    stack.push(me);
    const prev = document.activeElement as HTMLElement | null;
    const node = ref.current;
    const focusables = () =>
      Array.from(
        node?.querySelectorAll<HTMLElement>('button:not([disabled]), [href], input:not([disabled]), select, textarea, [tabindex]:not([tabindex="-1"])') ?? [],
      );
    const t = setTimeout(() => {
      const first = node?.querySelector<HTMLElement>('[data-autofocus]') ?? focusables()[0];
      first?.focus();
    }, 30);
    const onKey = (e: KeyboardEvent) => {
      if (stack[stack.length - 1] !== me) return;
      if (e.key === 'Escape' && onClose) {
        e.stopPropagation();
        onClose();
      }
      if (e.key === 'Tab' && node) {
        const f = focusables();
        if (f.length === 0) return;
        const first = f[0]!;
        const last = f[f.length - 1]!;
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    document.addEventListener('keydown', onKey);
    return () => {
      const at = stack.indexOf(me);
      if (at >= 0) stack.splice(at, 1);
      clearTimeout(t);
      document.removeEventListener('keydown', onKey);
      prev?.focus?.();
    };
  }, [open, onClose, ref]);
}

export interface OverlayProps {
  open: boolean;
  onClose?: () => void;
  title?: ReactNode;
  description?: ReactNode;
  children?: ReactNode;
  className?: string;
  /** Hide the close button (e.g. blocking dialogs). */
  dismissible?: boolean;
  testId?: string;
}

export function Modal({ open, onClose, title, description, children, className, dismissible = true, testId }: OverlayProps) {
  const ref = useRef<HTMLDivElement>(null);
  const titleId = useId();
  useFocusTrap(open, dismissible ? onClose : undefined, ref);
  return createPortal(
    <AnimatePresence>
      {open && (
        <motion.div className="fixed inset-0 z-[70] flex items-center justify-center p-4" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
          <div className="absolute inset-0 bg-night/50" onClick={dismissible ? onClose : undefined} aria-hidden />
          <motion.div
            ref={ref}
            role="dialog"
            aria-modal="true"
            aria-labelledby={title ? titleId : undefined}
            data-testid={testId}
            initial={{ opacity: 0, y: 16, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 8 }}
            transition={spring}
            className={cn('relative w-full max-w-md rounded-sheet bg-surface p-6 shadow-lift', className)}
          >
            {dismissible && onClose && <IconButton icon="x" label="Close" size="sm" className="absolute right-4 top-4" onClick={onClose} />}
            {title && (
              <h2 id={titleId} className="pr-10 font-display text-[28px] leading-[0.95]">
                {title}
              </h2>
            )}
            {description && <div className="mt-2 text-[15px] text-muted">{description}</div>}
            {children && <div className={cn(title || description ? 'mt-5' : '')}>{children}</div>}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body,
  );
}

/** Bottom sheet on mobile, centred dialog-ish panel on desktop. */
export function Sheet({ open, onClose, title, description, children, className, dismissible = true, testId, maxHeight = '85vh' }: OverlayProps & { maxHeight?: string }) {
  const ref = useRef<HTMLDivElement>(null);
  const titleId = useId();
  useFocusTrap(open, dismissible ? onClose : undefined, ref);
  return createPortal(
    <AnimatePresence>
      {open && (
        <motion.div className="fixed inset-0 z-[70] flex items-end justify-center sm:items-center sm:p-4" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
          <div className="absolute inset-0 bg-night/50" onClick={dismissible ? onClose : undefined} aria-hidden />
          <motion.div
            ref={ref}
            role="dialog"
            aria-modal="true"
            aria-labelledby={title ? titleId : undefined}
            data-testid={testId}
            initial={{ y: 40, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 40, opacity: 0 }}
            transition={spring}
            style={{ maxHeight }}
            className={cn(
              'relative flex w-full flex-col overflow-hidden rounded-t-sheet bg-surface shadow-lift sm:max-w-lg sm:rounded-sheet',
              className,
            )}
          >
            <div className="mx-auto mt-2.5 h-1 w-10 shrink-0 rounded-full bg-line sm:hidden" aria-hidden />
            <div className="overflow-y-auto px-5 pb-[calc(1.25rem+env(safe-area-inset-bottom))] pt-4 sm:px-6 sm:pb-6 sm:pt-6">
              {dismissible && onClose && <IconButton icon="x" label="Close" size="sm" className="absolute right-4 top-4" onClick={onClose} />}
              {title && (
                <h2 id={titleId} className="pr-10 font-display text-[28px] leading-[0.95]">
                  {title}
                </h2>
              )}
              {description && <div className="mt-1.5 text-[15px] text-muted">{description}</div>}
              <div className={cn(title || description ? 'mt-5' : '')}>{children}</div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body,
  );
}
